import React, { useEffect, useMemo, useRef, useState } from 'react';

import { useLanguage } from '../../i18n/LanguageContext';
import { getRecipeImageUrl } from '../../utils/recipeUtils';

const instructionModes = {
  FREE_TEXT: 'free-text',
  STEPS: 'steps',
};

const createIngredient = () => ({
  id: `ingredient-${Math.random().toString(36).slice(2)}`,
  name: '',
  quantity: '',
  healthyAlternatives: '',
});

const createStep = () => ({
  id: `step-${Math.random().toString(36).slice(2)}`,
  instruction: '',
  healthyAlternatives: '',
});

const createInitialMetaState = (recipe = null, currentUser = null) => {
  const currentUserDisplayName =
    currentUser?.fullName ||
    [currentUser?.firstName, currentUser?.lastName].filter(Boolean).join(' ').trim() ||
    currentUser?.login ||
    'Pninit';

  return {
    title: recipe?.title ?? '',
    description: recipe?.description ?? '',
    prepTime: recipe?.prepTime != null ? String(recipe.prepTime) : '20',
    cookTime: recipe?.cookTime != null ? String(recipe.cookTime) : '10',
    servings: recipe?.servings != null ? String(recipe.servings) : '2',
    tags: Array.isArray(recipe?.tags) ? recipe.tags.join(', ') : '',
    recipeUserId: recipe?.recipeUserId || currentUser?.id || 'user-123',
    recipeUserName: recipe?.recipeUserName || currentUserDisplayName,
  };
};

const hydrateIngredients = (recipe = null) => {
  if (Array.isArray(recipe?.ingredients) && recipe.ingredients.length) {
    return recipe.ingredients.map((ingredient) => ({
      id: `ingredient-${Math.random().toString(36).slice(2)}`,
      name: ingredient.name ?? '',
      quantity: ingredient.quantity ?? '',
      healthyAlternatives: Array.isArray(ingredient.healthyAlternatives)
        ? ingredient.healthyAlternatives.join(', ')
        : '',
    }));
  }

  return [createIngredient(), createIngredient()];
};

const hydrateSteps = (recipe = null) => {
  if (Array.isArray(recipe?.steps) && recipe.steps.length) {
    return recipe.steps.map((step) => ({
      id: `step-${Math.random().toString(36).slice(2)}`,
      instruction: step.instruction ?? '',
      healthyAlternatives: Array.isArray(step.healthyAlternatives)
        ? step.healthyAlternatives.join(', ')
        : '',
    }));
  }

  return [createStep()];
};

const splitAlternatives = (value = '') =>
  value
    .split(',')
    .map((entry) => entry.trim())
    .filter(Boolean);

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const SubmissionForm = ({
  onSubmit,
  onCancel = null,
  isSubmitting,
  initialRecipe = null,
  isEditMode = false,
  submitLabel = 'Publish Recipe',
  currentUser = null,
}) => {
  const { lang, t } = useLanguage();
  const s = t.submit;
  const editingRecipeId = initialRecipe?._id || initialRecipe?.id || null;
  const [heroFile, setHeroFile] = useState(null);
  const [heroPreview, setHeroPreview] = useState(
    initialRecipe ? getRecipeImageUrl(initialRecipe) : null
  );
  const [recipeMeta, setRecipeMeta] = useState(() => createInitialMetaState(initialRecipe, currentUser));
  const [ingredients, setIngredients] = useState(() => hydrateIngredients(initialRecipe));
  const [mode, setMode] = useState(instructionModes.STEPS);
  const [steps, setSteps] = useState(() => hydrateSteps(initialRecipe));
  const [freeText, setFreeText] = useState('');
  const [formError, setFormError] = useState(null);

  useEffect(
    () => () => {
      if (heroPreview?.startsWith('blob:')) {
        URL.revokeObjectURL(heroPreview);
      }
    },
    [heroPreview]
  );

  useEffect(() => {
    if (!editingRecipeId) {
      return;
    }

    setHeroFile(null);
    setHeroPreview(initialRecipe ? getRecipeImageUrl(initialRecipe) : null);
    setRecipeMeta(createInitialMetaState(initialRecipe, currentUser));
    setIngredients(hydrateIngredients(initialRecipe));
    setSteps(hydrateSteps(initialRecipe));
    setFreeText('');
    setMode(instructionModes.STEPS);
    setFormError(null);
  }, [editingRecipeId, initialRecipe, currentUser]);

  const handleHeroUpload = (event) => {
    const [file] = event.target.files;
    if (!file) {
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setFormError(s.imageTooLarge);
      event.target.value = '';
      return;
    }

    setFormError(null);
    setHeroFile(file);
    const previewUrl = URL.createObjectURL(file);
    setHeroPreview(previewUrl);
  };

  const handleMetaChange = (event) => {
    const { name, value } = event.target;
    setRecipeMeta((prev) => ({ ...prev, [name]: value }));
  };

  const updateIngredient = (id, field, value) => {
    setIngredients((prev) => prev.map((ingredient) => (ingredient.id === id ? { ...ingredient, [field]: value } : ingredient)));
  };

  const addIngredient = () => {
    setIngredients((prev) => [...prev, createIngredient()]);
  };

  const updateStepField = (id, field, value) => {
    setSteps((prev) => prev.map((step) => (step.id === id ? { ...step, [field]: value } : step)));
  };

  const addStep = () => {
    setSteps((prev) => [...prev, createStep()]);
  };

  const removeStep = (id) => {
    setSteps((prev) => (prev.length === 1 ? prev : prev.filter((step) => step.id !== id)));
  };

  const dragStepIndex = useRef(null);
  const touchDragOverIndex = useRef(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  const handleStepDragStart = (index) => {
    dragStepIndex.current = index;
  };

  const handleStepDragOver = (index) => {
    setDragOverIndex(index);
  };

  const handleStepDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleStepDrop = (index) => {
    const from = dragStepIndex.current;
    setDragOverIndex(null);
    if (from === null || from === index) return;
    setSteps((prev) => {
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(index, 0, moved);
      return next;
    });
    dragStepIndex.current = null;
  };

  const handleStepTouchStart = (index) => {
    dragStepIndex.current = index;
  };

  const handleStepTouchMove = (event) => {
    const touch = event.touches[0];
    const el = document.elementFromPoint(touch.clientX, touch.clientY);
    const card = el?.closest('.instruction-step-card');
    if (!card) return;
    const cards = Array.from(card.parentElement?.querySelectorAll('.instruction-step-card') ?? []);
    const overIndex = cards.indexOf(card);
    if (overIndex !== -1 && overIndex !== touchDragOverIndex.current) {
      touchDragOverIndex.current = overIndex;
      setDragOverIndex(overIndex);
    }
  };

  const handleStepTouchEnd = () => {
    const from = dragStepIndex.current;
    const to = touchDragOverIndex.current;
    setDragOverIndex(null);
    dragStepIndex.current = null;
    touchDragOverIndex.current = null;
    if (from === null || to === null || from === to) return;
    setSteps((prev) => {
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
  };

  const moveIngredient = (id, direction) => {
    setIngredients((prev) => {
      const index = prev.findIndex((ingredient) => ingredient.id === id);
      if (index < 0) return prev;
      const newIndex = direction === 'up' ? index - 1 : index + 1;
      if (newIndex < 0 || newIndex >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[newIndex]] = [next[newIndex], next[index]];
      return next;
    });
  };


  const resetForm = () => {
    if (heroPreview?.startsWith('blob:')) {
      URL.revokeObjectURL(heroPreview);
    }
    setHeroFile(null);
    setHeroPreview(null);
    setRecipeMeta(createInitialMetaState(null, currentUser));
    setIngredients(hydrateIngredients(null));
    setSteps(hydrateSteps(null));
    setFreeText('');
    setMode(instructionModes.STEPS);
  };

  const uploaderBackground = useMemo(
    () =>
      heroPreview ||
      'https://lh3.googleusercontent.com/aida-public/AB6AXuDCEOK9HV_F58sVAdYH73mq0HHzkz1d2acbqpdaPEDYQbXRDdylzA1F0lR20Va7lsoSjHoDa9cHkURPVtOahN2MehaWZR0O_d4ac8m7bUytakLSWJ-N7IL27gSzTZ-tZc49G0ORjA5KBlrIJ_789oh4_8IYL2RK7CUmgCuUsPp-AIS_YOHlWPf-8yVh_9pcP7_lyav6QnahRaQWcCRwVbwLT0E6mwT6ouXW55Bgqjdf9RP5sVsWPEB1NKriR9JXl1ZK-eaEp-NUtV0',
    [heroPreview]
  );

  const hasIngredients = useMemo(
    () => ingredients.some((ingredient) => ingredient.name.trim().length > 0),
    [ingredients]
  );

  const hasInstructions = useMemo(
    () =>
      mode === instructionModes.FREE_TEXT
        ? freeText.trim().length > 0
        : steps.some((step) => step.instruction.trim().length > 0),
    [freeText, mode, steps]
  );

  const isSubmitDisabled = isSubmitting || !hasIngredients || !hasInstructions;

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!onSubmit) {
      return;
    }

    setFormError(null);

    if (!hasIngredients || !hasInstructions) {
      setFormError(s.validationError);
      return;
    }

    const normalizedIngredients = ingredients
      .filter((ingredient) => ingredient.name.trim().length)
      .map((ingredient) => {
        const healthyAlternatives = splitAlternatives(ingredient.healthyAlternatives);
        const entry = {
          name: ingredient.name.trim(),
          quantity: ingredient.quantity.trim() || undefined,
        };

        if (healthyAlternatives.length) {
          entry.healthyAlternatives = healthyAlternatives;
        }

        return entry;
      });

    const normalizedSteps =
      mode === instructionModes.STEPS
        ? steps
            .filter((step) => step.instruction.trim().length)
            .map((step) => {
              const healthyAlternatives = splitAlternatives(step.healthyAlternatives);
              const entry = { instruction: step.instruction.trim() };
              if (healthyAlternatives.length) {
                entry.healthyAlternatives = healthyAlternatives;
              }
              return entry;
            })
        : freeText.trim()
          ? [{ instruction: freeText.trim() }]
          : [];

    const tags = splitAlternatives(recipeMeta.tags);

    const payload = {
      title: recipeMeta.title.trim(),
      description: recipeMeta.description.trim(),
      ingredients: normalizedIngredients,
      steps: normalizedSteps,
      tags,
      prepTime: toNumber(recipeMeta.prepTime || '20'),
      cookTime: toNumber(recipeMeta.cookTime || '10'),
      servings: toNumber(recipeMeta.servings || '2'),
      recipeUserId: recipeMeta.recipeUserId.trim() || 'user-123',
      recipeUserName: recipeMeta.recipeUserName.trim() || 'Pninit',
      lang,
    };

    try {
      await onSubmit({ payload, imageFile: heroFile });
      if (!isEditMode) {
        resetForm();
      }
    } catch (err) {
      setFormError(err?.message ?? (isEditMode ? s.errorEdit : s.errorCreate));
    }
  };

  return (
    <form className="submission-form" onSubmit={handleSubmit}>
      <div className="hero-upload">
        <label htmlFor="hero-upload-input" className="hero-upload-field">
          <input id="hero-upload-input" type="file" accept="image/*" onChange={handleHeroUpload} />
          <div className="hero-upload-visual" style={{ backgroundImage: `url(${uploaderBackground})` }}>
            <div className="hero-upload-overlay" />
            <div className="hero-upload-copy">
              <span className="material-symbols-rounded" aria-hidden="true">
                add_a_photo
              </span>
              <p>{s.uploadImage}</p>
            </div>
          </div>
        </label>
      </div>

      <div className="submission-field-stack">
        <div className="submission-field">
          <label htmlFor="recipe-title">{s.titleLabel}</label>
          <input
            id="recipe-title"
            name="title"
            type="text"
            placeholder={s.titlePlaceholder}
            value={recipeMeta.title}
            onChange={handleMetaChange}
            required
          />
        </div>

        <div className="submission-field">
          <label htmlFor="recipe-description">{s.descriptionLabel}</label>
          <textarea
            id="recipe-description"
            name="description"
            rows={3}
            className="body-textarea"
            placeholder={s.descriptionPlaceholder}
            value={recipeMeta.description}
            onChange={handleMetaChange}
          />
        </div>

        <div className="submission-field-grid">
          <div className="submission-field">
            <label htmlFor="prep-time">{s.prepTimeLabel}</label>
            <div className="meta-input">
              <span className="material-symbols-rounded" aria-hidden="true">
                schedule
              </span>
              <input
                id="prep-time"
                name="prepTime"
                type="number"
                min="0"
                placeholder="20"
                value={recipeMeta.prepTime}
                onChange={handleMetaChange}
              />
            </div>
          </div>
          <div className="submission-field">
            <label htmlFor="cook-time">{s.cookTimeLabel}</label>
            <div className="meta-input">
              <span className="material-symbols-rounded" aria-hidden="true">
                restaurant
              </span>
              <input
                id="cook-time"
                name="cookTime"
                type="number"
                min="0"
                placeholder="10"
                value={recipeMeta.cookTime}
                onChange={handleMetaChange}
              />
            </div>
          </div>
          <div className="submission-field">
            <label htmlFor="servings">{s.servingsLabel}</label>
            <div className="meta-input">
              <span className="material-symbols-rounded" aria-hidden="true">
                groups
              </span>
              <input
                id="servings"
                name="servings"
                type="number"
                min="1"
                placeholder="2"
                value={recipeMeta.servings}
                onChange={handleMetaChange}
              />
            </div>
          </div>
          <div className="submission-field">
            <label htmlFor="recipe-tags">{s.tagsLabel}</label>
            <input
              id="recipe-tags"
              name="tags"
              type="text"
              placeholder={s.tagsPlaceholder}
              value={recipeMeta.tags}
              onChange={handleMetaChange}
            />
            <small>{s.tagsHelp}</small>
          </div>
        </div>

        <div className="submission-field">
          <label>{s.ingredientsLabel}</label>
          <div className="ingredients-list">
            {ingredients.map((ingredient, index) => (
              <div key={ingredient.id} className="ingredient-item">
                <span className="ingredient-bullet" />
                <div className="ingredient-fields">
                  <div className="ingredient-input-grid">
                    <input
                      type="text"
                      placeholder={
                        index === 0 ? s.ingredientNamePlaceholder : s.nextIngredientPlaceholder
                      }
                      value={ingredient.name}
                      onChange={(event) => updateIngredient(ingredient.id, 'name', event.target.value)}
                    />
                    <input
                      type="text"
                      placeholder={s.quantityPlaceholder}
                      value={ingredient.quantity}
                      onChange={(event) => updateIngredient(ingredient.id, 'quantity', event.target.value)}
                    />
                  </div>
                  <input
                    className="ingredient-alt-input"
                    type="text"
                    placeholder={s.healthyAlternativesPlaceholder}
                    value={ingredient.healthyAlternatives}
                    onChange={(event) =>
                      updateIngredient(ingredient.id, 'healthyAlternatives', event.target.value)
                    }
                  />
                </div>
                <div className="ingredient-actions">
                  <button
                    type="button"
                    className="icon-button subtle"
                    aria-label={s.moveUp}
                    disabled={index === 0}
                    onClick={() => moveIngredient(ingredient.id, 'up')}
                  >
                    <span className="material-symbols-rounded" aria-hidden="true">
                      arrow_upward
                    </span>
                  </button>
                  <button
                    type="button"
                    className="icon-button subtle"
                    aria-label={s.moveDown}
                    disabled={index === ingredients.length - 1}
                    onClick={() => moveIngredient(ingredient.id, 'down')}
                  >
                    <span className="material-symbols-rounded" aria-hidden="true">
                      arrow_downward
                    </span>
                  </button>
                </div>
              </div>
            ))}
            <button type="button" className="ghost-button" onClick={addIngredient}>
              <span className="material-symbols-rounded" aria-hidden="true">
                add
              </span>
              {s.addIngredient}
            </button>
          </div>
        </div>

        <div className="instructions-toggle">
          {Object.values(instructionModes).map((instructionMode) => (
            <button
              key={instructionMode}
              type="button"
              className={`instruction-mode-button${mode === instructionMode ? ' is-active' : ''}`}
              onClick={() => setMode(instructionMode)}
            >
              {instructionMode === instructionModes.FREE_TEXT ? s.modeFreeText : s.modeSteps}
            </button>
          ))}
        </div>

        <div className="submission-field">
          <label>{s.instructionsLabel}</label>
          {mode === instructionModes.FREE_TEXT ? (
            <textarea
              rows={6}
              className="instruction-textarea"
              placeholder={s.instructionsPlaceholder}
              value={freeText}
              onChange={(event) => setFreeText(event.target.value)}
            />
          ) : (
            <div className="instruction-steps">
              {steps.map((step, index) => (
                <div
                  key={step.id}
                  className={`instruction-step-card${dragOverIndex === index ? ' drag-over' : ''}`}
                  draggable
                  onDragStart={() => handleStepDragStart(index)}
                  onDragOver={(e) => { e.preventDefault(); handleStepDragOver(index); }}
                  onDragLeave={handleStepDragLeave}
                  onDrop={() => handleStepDrop(index)}
                  onTouchStart={() => handleStepTouchStart(index)}
                  onTouchMove={handleStepTouchMove}
                  onTouchEnd={handleStepTouchEnd}
                >
                  <span className="instruction-step-index">{index + 1}</span>
                  <div className="instruction-step-body">
                    <textarea
                      rows={2}
                      placeholder={index === 0 ? s.firstStepPlaceholder : s.nextStepPlaceholder}
                      value={step.instruction}
                      onInput={(event) => {
                        event.target.style.height = 'auto';
                        event.target.style.height = `${event.target.scrollHeight}px`;
                      }}
                      onChange={(event) => updateStepField(step.id, 'instruction', event.target.value)}
                    />
                    <input
                      type="text"
                      className="instruction-alt-input"
                      placeholder={s.healthyAlternativesPlaceholder}
                      value={step.healthyAlternatives}
                      onChange={(event) => updateStepField(step.id, 'healthyAlternatives', event.target.value)}
                    />
                  </div>
                  <div className="instruction-step-actions">
                    <span className="material-symbols-rounded drag-handle" aria-hidden="true">
                      drag_indicator
                    </span>
                    <button
                      type="button"
                      className="icon-button subtle step-delete"
                      aria-label={s.removeStep}
                      onClick={() => removeStep(step.id)}
                    >
                      <span className="material-symbols-rounded" aria-hidden="true">
                        delete
                      </span>
                    </button>
                  </div>
                </div>
              ))}
              <button type="button" className="dashed-button" onClick={addStep}>
                <span className="material-symbols-rounded" aria-hidden="true">
                  add_circle
                </span>
                {s.addStep}
              </button>
            </div>
          )}
        </div>
      </div>

      {formError ? (
        <p className="form-error" role="alert">
          {formError}
        </p>
      ) : null}

      <div className="submission-actions">
        {lang === 'he' && onCancel ? (
          <button type="button" className="cancel-action" onClick={onCancel} disabled={isSubmitting}>
            {s.cancelButton}
          </button>
        ) : null}
        <button type="submit" className="primary-action" disabled={isSubmitDisabled}>
          {isSubmitting ? (isEditMode ? s.updating : s.publishing) : submitLabel}
        </button>
        {lang !== 'he' && onCancel ? (
          <button type="button" className="cancel-action" onClick={onCancel} disabled={isSubmitting}>
            {s.cancelButton}
          </button>
        ) : null}
      </div>
    </form>
  );
};

export default SubmissionForm;
